import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { Form, Formik, FormikValues } from "formik";
import { sendToSlackSchema } from "formValidations/sendToSlackSchema";
import { useToast } from "hooks/useToast";
import { useMemo } from "react";
import {
  useGetAllChannelMemberQuery,
  useSendMessageToSlackMutation,
} from "store/apis/chat";
import {
  MessageUIComponentProps,
  useChannelStateContext,
} from "stream-chat-react";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";

export const SendToSlack = ({
  isOpen,
  close,
  message,
}: {
  message: MessageUIComponentProps["message"] & {
    forwarded_message?: MessageUIComponentProps["message"];
  };
} & ModalProps) => {
  const { channel } = useChannelStateContext();
  const groupId = channel.data?.group_uuid! as string;
  const { data: members, isLoading } = useGetAllChannelMemberQuery(
    {
      groupId: groupId,
      channelId: channel.id!,
    },
    {
      skip: !channel.id || !channel.data?.group_uuid,
    }
  );
  const membersWithSlackAccess = members?.filter(
    ({ slack_access, is_archived_user }) => slack_access && !is_archived_user
  );
  const [sendMessageToSlack, { isLoading: isSending }] =
    useSendMessageToSlackMutation();
  const { alertToast, successToast } = useToast();

  const sendMessage = async (values: FormikValues) => {
    try {
      const payload = {
        to_users: values.members.map((email: string) => ({ email })),
      };
      await sendMessageToSlack({
        channelId: channel.id!,
        messageId: message.id,
        groupId,
        payload,
      }).unwrap();
      successToast({ message: "Message sent successfully!" });
      close();
    } catch (e) {
      alertToast({ message: (e as BackendError).data?.error?.message });
    }
  };

  const options = useMemo(
    () =>
      membersWithSlackAccess?.map((member) => ({
        label: member.name,
        value: member.email,
      }))!,
    [membersWithSlackAccess]
  );

  return (
    <Modal.Root open={isOpen} onOpenChange={close} modal={false}>
      <Modal.Content useCustomOverlay>
        <Formik
          initialValues={{ members: [] as string[] }}
          onSubmit={sendMessage}
          validationSchema={sendToSlackSchema}
          validateOnChange
          validateOnMount={false}
        >
          {({ setFieldValue, submitForm, values }) => {
            return (
              <Form>
                <Modal.Header>
                  <Modal.Title>Send to Slack</Modal.Title>
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body className="t-space-y-4 t-overflow-hidden t-max-h-[700px]">
                  <div className="t-text-body-sm t-text-text-60">
                    Please select the customers & they will receive the message
                    on Slack.
                  </div>
                  <Combobox
                    withForm
                    label="To"
                    components={{
                      ClearIndicator: () => null,
                    }}
                    isMulti
                    placeholder="Select"
                    name="members"
                    required
                    value={
                      values.members.length > 0
                        ? options.filter((option) =>
                            (values.members as string[]).includes(option.value)
                          )
                        : null
                    }
                    options={options}
                    actions={
                      <div>
                        <Button
                          customType="text"
                          size="small"
                          onClick={() => setFieldValue("members", [])}
                        >
                          Reset Selection
                        </Button>
                        <Button
                          customType="text"
                          size="small"
                          onClick={() =>
                            setFieldValue(
                              "members",
                              options.map((option) => option.value)
                            )
                          }
                        >
                          Select All
                        </Button>
                      </div>
                    }
                    isLoading={isLoading}
                    menuPortalTarget={document.body}
                    styles={{
                      menuList: (base) => ({
                        ...base,
                        maxHeight: "260px",
                        overflowY: "auto",
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        maxHeight: "120px",
                        overflowY: "auto",
                      }),
                    }}
                  />
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Modal.RawClose asChild>
                    <Button>Cancel</Button>
                  </Modal.RawClose>
                  <Button
                    customType="primary"
                    type="submit"
                    disabled={isSending}
                    isLoading={isSending}
                    onClick={submitForm}
                  >
                    Confirm
                  </Button>
                </Modal.FooterButtonGroup>
              </Form>
            );
          }}
        </Formik>
      </Modal.Content>
    </Modal.Root>
  );
};
